/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 에디터의 IME 조합(한글 등) 이벤트를 처리한다.
 *
 * @file /src/rootin/events/Composition.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 27.
 */
import { Editor } from '@rootin/core/Editor';
import { Dom } from '@utils/Dom';

export class Composition {
    /**
     * 조합 진행여부
     */
    #composing: boolean = false;

    /**
     * 조합 중 발생한 키입력 등을 조합이 끝난 뒤 실행하기 위해 예약해둔다.
     */
    pendingAction: (() => void) | null = null;

    /**
     * 에디터의 IME 조합(한글 등) 이벤트를 처리한다.
     *
     * @param editor - 이벤트를 정의할 에디터
     */
    constructor(public editor: Editor) {}

    /**
     * 이벤트 리스너를 바인딩한다.
     *
     * @param $container - 이벤트를 받을 컨테이너 DOM
     */
    listen($container: Dom): void {
        $container.on('compositionstart', () => {
            this.#composing = true;
        });

        $container.on('compositionend', () => {
            this.#composing = false;
            this.#flushPendingActionNextFrame();
        });
    }

    /**
     * 지금 IME 조합 중인지 확인한다.
     *
     * @returns 조합 중이면 true
     */
    isComposing(): boolean {
        return this.#composing;
    }

    /**
     * 지금 조합 중이 아니면 함수를 바로 실행하고, 조합 중이면 조합이 끝난 뒤 실행되도록 예약한다.
     *
     * @param fn - 실행할 함수
     */
    safeAction(fn: () => void): void {
        if (this.#composing === true) {
            this.pendingAction = fn;
            return;
        }

        fn();
    }

    /**
     * 조합이 완료되었을 때 1프레임을 기다려 또다른 조합이 시작되었는지 판단한다.
     */
    #flushPendingActionNextFrame(): void {
        if (this.pendingAction === null) {
            return;
        }

        requestAnimationFrame(() => {
            if (this.pendingAction === null || this.#composing === true) {
                return;
            }

            const pending = this.pendingAction;
            this.pendingAction = null;
            pending();
        });
    }
}
